import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Monitor, TrendingUp, TrendingDown, Printer, Users, RefreshCw, AlertTriangle, X, Calendar, ArrowRight, IndianRupee } from 'lucide-react';
import { ResponsiveContainer, BarChart, Bar, XAxis, Tooltip } from 'recharts';
import { API, useAuth } from '../App';

const RANGE_OPTIONS = [
  { value: '7d', label: '7d' },
  { value: '30d', label: '30d' },
  { value: '90d', label: '90d' },
  { value: 'mtd', label: 'MTD' },
  { value: 'ytd', label: 'YTD' },
  { value: 'all', label: 'All' },
];

const STORAGE_KEY = 'dashboardFilters';

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inventoryAlerts, setInventoryAlerts] = useState([]);
  const [dismissedAlerts, setDismissedAlerts] = useState(() => {
    try {
      return JSON.parse(sessionStorage.getItem('dismissedInventoryAlerts') || '[]');
    } catch {
      return [];
    }
  });
  const [kiosksOptions, setKiosksOptions] = useState([]);
  const [filters, setFilters] = useState(() => {
    try {
      const saved = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null');
      return saved || { range: '30d', kiosk_id: '' };
    } catch {
      return { range: '30d', kiosk_id: '' };
    }
  });
  const { token } = useAuth();
  const navigate = useNavigate();

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const params = { date_range: filters.range };
      if (filters.kiosk_id) params.kiosk_id = filters.kiosk_id;
      const response = await axios.get(`${API}/stats/overview`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      setStats(response.data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
    setLoading(false);
  }, [filters, token]);

  const fetchInventoryAlerts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/inventory/alerts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInventoryAlerts(response.data.alerts || []);
    } catch (err) {
      console.error('Failed to fetch inventory alerts:', err);
    }
  }, [token]);

  const fetchKiosksList = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/kiosks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setKiosksOptions(response.data.map((k) => ({ id: k.id, name: k.name })));
    } catch (err) {
      console.error('Failed to fetch kiosks:', err);
    }
  }, [token]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    fetchInventoryAlerts();
    fetchKiosksList();
  }, [fetchInventoryAlerts, fetchKiosksList]);

  useEffect(() => {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
  }, [filters]);

  const dismissAlert = (kioskId) => {
    const updated = [...dismissedAlerts, kioskId];
    setDismissedAlerts(updated);
    sessionStorage.setItem('dismissedInventoryAlerts', JSON.stringify(updated));
  };

  const visibleAlerts = inventoryAlerts.filter((a) => !dismissedAlerts.includes(a.kiosk_id));

  const statCards = stats ? [
    {
      title: 'Total Booths',
      value: stats.total_kiosks,
      subtext: `${stats.online_kiosks} online`,
      icon: <Monitor className="w-6 h-6" />,
      color: 'bg-rose-100',
      onClick: () => navigate('/admin/kiosks'),
    },
    {
      title: 'Revenue',
      value: `₹${(stats.total_revenue || 0).toLocaleString()}`,
      subtext: filters.range === 'all' ? 'All time' : `Last ${filters.range.toUpperCase()}`,
      icon: <TrendingUp className="w-6 h-6" />,
      color: 'bg-green-100',
    },
    {
      title: 'Prints',
      value: (stats.total_prints || 0).toLocaleString(),
      subtext: filters.range === 'all' ? 'All time' : `Last ${filters.range.toUpperCase()}`,
      icon: <Printer className="w-6 h-6" />,
      color: 'bg-blue-100',
    },
    {
      title: 'Pending Leads',
      value: stats.pending_contacts,
      subtext: 'New inquiries',
      icon: <Users className="w-6 h-6" />,
      color: 'bg-amber-100',
      onClick: () => navigate('/admin/contacts'),
    },
  ] : [];

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="admin-dashboard">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-5 sm:mb-6">
        <div>
          <h1 className="font-serif text-2xl sm:text-3xl text-charcoal-900">Dashboard</h1>
          <p className="text-charcoal-800/60 mt-1 text-sm sm:text-base">Welcome back</p>
        </div>
        <button
          onClick={fetchStats}
          className="flex items-center justify-center gap-2 px-4 py-2 text-charcoal-800/60 hover:text-charcoal-900 transition-colors bg-white rounded-lg shadow-sm sm:shadow-none sm:bg-transparent"
          data-testid="refresh-stats-btn"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          <span className="text-sm sm:text-base">Refresh</span>
        </button>
      </div>

      {/* Today pulse — live IST-day snapshot linking into full analytics */}
      <TodayPulse token={token} onOpen={() => navigate('/admin/analytics')} />

      {/* Filters */}
      <div className="card-dashboard mb-5 sm:mb-6 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4" data-testid="dashboard-filters">
        <div className="flex items-center gap-2 text-charcoal-800/60 text-sm">
          <Calendar className="w-4 h-4" />
          <span>Range</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {RANGE_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => setFilters({ ...filters, range: opt.value })}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                filters.range === opt.value
                  ? 'bg-charcoal-900 text-white'
                  : 'bg-cream-50 text-charcoal-800/70 hover:bg-cream-100'
              }`}
              data-testid={`range-${opt.value}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <div className="sm:ml-auto flex items-center gap-2">
          <span className="text-charcoal-800/60 text-sm">Booth</span>
          <select
            value={filters.kiosk_id}
            onChange={(e) => setFilters({ ...filters, kiosk_id: e.target.value })}
            className="px-3 py-1.5 bg-white border border-charcoal-900/10 rounded-lg text-sm focus:outline-none focus:border-charcoal-900/30"
            data-testid="filter-kiosk-select"
          >
            <option value="">All booths</option>
            {kiosksOptions.map((k) => (
              <option key={k.id} value={k.id}>{k.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Stat cards */}
      {loading ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="card-dashboard animate-pulse">
              <div className="h-10 w-10 sm:h-12 sm:w-12 bg-gray-200 rounded-xl mb-3 sm:mb-4"></div>
              <div className="h-6 sm:h-8 w-16 sm:w-20 bg-gray-200 rounded mb-2"></div>
              <div className="h-3 sm:h-4 w-12 sm:w-16 bg-gray-100 rounded"></div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
          {statCards.map((card, index) => (
            <div
              key={index}
              onClick={card.onClick}
              className={`card-dashboard ${card.onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}
              data-testid={`stat-card-${index}`}
            >
              <div className={`w-10 h-10 sm:w-12 sm:h-12 ${card.color} rounded-xl flex items-center justify-center mb-3 sm:mb-4`}>
                {card.icon}
              </div>
              <div className="font-serif text-2xl sm:text-3xl text-charcoal-900 mb-1">{card.value}</div>
              <div className="text-xs sm:text-sm text-charcoal-800/60">{card.title}</div>
              <div className="text-xs text-charcoal-800/40 mt-1 hidden sm:block">{card.subtext}</div>
            </div>
          ))}
        </div>
      )}

      {/* Inventory Alerts */}
      {visibleAlerts.length > 0 && (
        <div className="mt-6 sm:mt-8" data-testid="inventory-alerts">
          <h2 className="font-serif text-lg sm:text-xl text-charcoal-900 mb-3">Inventory Alerts</h2>
          <div className="space-y-2">
            {visibleAlerts.map((alert) => (
              <div
                key={alert.kiosk_id}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl border ${
                  alert.severity === 'critical'
                    ? 'bg-red-50 border-red-200'
                    : 'bg-amber-50 border-amber-200'
                }`}
                data-testid={`inventory-alert-${alert.kiosk_id}`}
              >
                <AlertTriangle className={`w-5 h-5 flex-shrink-0 ${
                  alert.severity === 'critical' ? 'text-red-500' : 'text-amber-500'
                }`} />
                <button
                  onClick={() => navigate(`/admin/kiosks/${alert.kiosk_id}`)}
                  className="flex-1 min-w-0 text-left"
                  data-testid={`inventory-alert-link-${alert.kiosk_id}`}
                >
                  <p className="text-sm font-medium text-charcoal-900 truncate">
                    {alert.kiosk_name} — {alert.remaining} prints remaining
                  </p>
                  <p className="text-xs text-charcoal-800/60">
                    {alert.location} &middot; {alert.severity === 'critical' ? 'Refill urgently' : 'Refill soon'}
                  </p>
                </button>
                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                  alert.severity === 'critical'
                    ? 'bg-red-100 text-red-700'
                    : 'bg-amber-100 text-amber-700'
                }`}>
                  {alert.severity}
                </span>
                <button
                  onClick={() => dismissAlert(alert.kiosk_id)}
                  className="p-1 rounded-md text-charcoal-800/40 hover:text-charcoal-900 hover:bg-white/60 transition-colors"
                  aria-label="Dismiss alert"
                  data-testid={`dismiss-alert-${alert.kiosk_id}`}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="mt-8 sm:mt-12">
        <h2 className="font-serif text-lg sm:text-xl text-charcoal-900 mb-3 sm:mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          <button
            onClick={() => navigate('/admin/kiosks')}
            className="card-dashboard text-left hover:shadow-md transition-shadow group"
            data-testid="quick-action-kiosks"
          >
            <h3 className="font-medium text-charcoal-900 group-hover:text-rose-500 transition-colors text-sm sm:text-base">
              Manage Booths →
            </h3>
            <p className="text-xs sm:text-sm text-charcoal-800/60 mt-1">View, edit, and configure your photobooths</p>
          </button>
          <button
            onClick={() => navigate('/admin/contacts')}
            className="card-dashboard text-left hover:shadow-md transition-shadow group"
            data-testid="quick-action-leads"
          >
            <h3 className="font-medium text-charcoal-900 group-hover:text-rose-500 transition-colors text-sm sm:text-base">
              View Leads →
            </h3>
            <p className="text-xs sm:text-sm text-charcoal-800/60 mt-1">Check new venue inquiries</p>
          </button>
          <button
            onClick={() => navigate('/admin/software')}
            className="card-dashboard text-left hover:shadow-md transition-shadow group"
            data-testid="quick-action-software"
          >
            <h3 className="font-medium text-charcoal-900 group-hover:text-rose-500 transition-colors text-sm sm:text-base">
              Booth Software →
            </h3>
            <p className="text-xs sm:text-sm text-charcoal-800/60 mt-1">Download &amp; install on a new photobooth</p>
          </button>
        </div>
      </div>
    </div>
  );
};

const DAY_MS = 86400000;
const IST_OFF = 330 * 60000;
const istMidnightMs = () => {
  const d = new Date(Date.now() + IST_OFF);
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()) - IST_OFF;
};

const TodayPulse = ({ token, onOpen }) => {
  const [data, setData] = useState(null);

  useEffect(() => {
    const s = istMidnightMs();
    const params = new URLSearchParams({
      start: new Date(s).toISOString(),
      end: new Date(s + DAY_MS).toISOString(),
      prev_start: new Date(s - DAY_MS).toISOString(),
      prev_end: new Date(s).toISOString(),
      granularity: 'hour',
    });
    axios.get(`${API}/analytics/overview?${params}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setData(r.data))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!data) return null;
  const cur = data.kpis.current;
  const prev = data.kpis.previous;
  const delta = prev && prev.revenue > 0 ? ((cur.revenue - prev.revenue) / prev.revenue) * 100 : null;
  const up = delta == null || delta >= 0;

  return (
    <div
      className="relative overflow-hidden rounded-2xl mb-5 sm:mb-6 p-5 sm:p-6 text-white cursor-pointer group"
      style={{ background: 'linear-gradient(120deg, #6D28D9 0%, #8B5CF6 45%, #A855F7 100%)' }}
      onClick={onOpen}
      data-testid="today-pulse-card"
    >
      <div className="flex items-center justify-between flex-wrap gap-4 relative z-10">
        <div className="flex items-center gap-6 sm:gap-10 flex-wrap">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-white/70 font-semibold">Today's revenue (IST)</div>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-3xl font-bold" data-testid="today-pulse-revenue">₹{Math.round(cur.revenue).toLocaleString('en-IN')}</span>
              {delta != null && (
                <span className={`inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 text-[11px] font-bold ${up ? 'bg-emerald-400/25 text-emerald-100' : 'bg-rose-400/25 text-rose-100'}`}>
                  {up ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                  {Math.abs(delta).toFixed(0)}% vs yesterday
                </span>
              )}
            </div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wider text-white/70 font-semibold">Orders</div>
            <div className="text-2xl font-bold mt-1" data-testid="today-pulse-orders">{cur.paid_orders}</div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wider text-white/70 font-semibold">Prints</div>
            <div className="text-2xl font-bold mt-1" data-testid="today-pulse-prints">{cur.prints}</div>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="hidden md:block w-48 h-16">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.trend}>
                <Bar dataKey="revenue" fill="rgba(255,255,255,0.85)" radius={[2, 2, 0, 0]} />
                <XAxis dataKey="label" hide />
                <Tooltip cursor={false} content={({ active, payload }) => active && payload?.length ? (
                  <div className="bg-white text-charcoal-900 text-xs rounded-lg px-2 py-1 shadow">₹{payload[0].value} · {payload[0].payload.label}</div>
                ) : null} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <span className="inline-flex items-center gap-1.5 text-sm font-semibold bg-white/15 rounded-full px-4 py-2 group-hover:bg-white/25 transition-colors" data-testid="today-pulse-open-analytics">
            Full analytics <ArrowRight className="w-4 h-4" />
          </span>
        </div>
      </div>
      <IndianRupee className="absolute -right-4 -bottom-6 w-36 h-36 text-white/10 rotate-12" />
    </div>
  );
};

export default Dashboard;
